import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
    name: 'process-interpolation-on-string',
})
export class ProcessInterpolationOnStringPipe implements PipeTransform {
    private readonly regexFunction = /(\w+(\.\w+)*)\(.*\)/g;

    private readonly regexInterpolationContent = /(\w+(\[\d])*\.)*([\w()[\]])+/g;

    private readonly regexpInner = /[^{].+\.[^ +}]+/g;

    private readonly regexpOuter = /{{([^{]+)}}|"(\w+(\.\w+)*)\(.*\)"/g;

    private readonly regexSplit = /split\(.+\)\[.+]/g;

    /**
     * Procesa un string que contiene interpolación. Ej: "Proveedor: {{ provider.name }}, {{ service.getBrand() }}"
     * @param str Texto con interpolación a procesar.
     * @param data Objeto con los datos necesarios para las propiedades, se pueden incluir funciones
     * en él. Ej: { provider: { name: 'Carlos'}, getOrders: () => this.orders, service: { getBrand: () =>
     * 'Massimo Dutti' } }
     * @returns String procesado. Ej: "Proveedor: Carlos, Massimo Dutti".
     */
    public transform(str: string, data: unknown): string {
        // Sustituye diferentes maneras de añadir datos a la template de legacy a interpolación para ser procesado
        const reformatText = (text: string) => {
            let content = text;

            // HTML binding
            content = content.replace(/<.+ng-bind-html=".+".*></g, (match: string) => {
                return this.processHtmlBinding(match);
            });

            // NG Repeat
            content.match(/<.+ng-repeat=".+".*>/g)?.forEach((match: string) => {
                const contentReplaced = this.processNgRepeat(match, content, data);
                // Reemplaza la parte del contenido original con el ng-repeat con el contenido procesado
                content = content.replace(contentReplaced.originalContent, contentReplaced.resultContent);
            });

            // String concatenation ( {{string + ' ' + string}} => {{ string }} {{ string }} )
            content = content.replace(/{{.+\+.+}}/g, (_match: string, _: any) => {
                return this.processStringConcat(_match);
            });

            return content;
        };

        // Encuentra en el contenido introducido en el tranformer instancias de lo que debe procesar
        // y lo pasa a la función principal que se encargará de clasificar su tipo y procesarla adecuadamente
        return reformatText(str).replace(this.regexpOuter, (match: string, _: any) => {
            const _match = match.replace(/["'+{}]/g, '');
            return _match.replace(this.regexpInner, (innerMatch, __) => {
                return this.getDataFromMatch(innerMatch, data);
            });
        });
    }

    /**
     * Manda a procesar la información según su tipo (propiedades, funciones o .split)
     * @param match Información a clasificar
     * @param data Objeto que contiene los datos necesarios para el procesamiento
     * @returns Dato obtenido del procesamiento
     */
    private getDataFromMatch(match: string, data: unknown) {
        const innerMatch = match.trim();

        // Si incluye un .split al final (Ej. TermsCondCtrl.address.city.split(#)) lo elimina del array de
        // propiedades a procesar y luego obtiene la información del separador e indice del split para hacerlo
        // con la propiedad obtenida de getDataFromProperty()
        if (this.regexSplit.exec(innerMatch)) {
            const props = innerMatch.split('.');
            const splitParams = this.getSplitParams(props);
            return this.getDataFromProperty(props, data).split(splitParams.divider)[splitParams.position];
        }
        // Si incluye una función al final (Ej. ItxService.getData()) accede a los datos para obtener la
        // función, obtiene los valores de los parámetros si los tiene y la llama
        if (this.regexFunction.exec(innerMatch)) {
            return this.getDataFromFunction(innerMatch, data);
        }
        // Si tiene un formato simple (Ej. TermsCondCtrl.address.firstName)
        const props = innerMatch.split('.');
        return this.getDataFromProperty(props, data);
    }

    /**
    * Accede y devuelve los datos de un objeto a los que hacen referencia las propiedades que se le introducen
    * @param selectionArray Array que contiene las propiedades para acceder a los datos (Ej. ['shopCart','items'] == shopCart.items)
    * @param obj Objeto que contiene los datos a acceder
    * @returns Datos encontrados, ' ' en caso de que no existan
    */
    private getDataFromProperty(selectionArray: string[], obj) {
        let _obj = obj;
        selectionArray.forEach((key: string) => {
            // Si es un acceso al indice de un array
            if (key.match(/.+\[.+]/g)) {
                const objFound = _obj[key.split('[')[0]];
                if (objFound) {
                    _obj = objFound[parseInt(key.split('[')[1].split(']')[0])];
                } else {
                    _obj = '';
                }
            // Si se encuentra la propiedad en el objeto
            } else if (_obj[key]) {
                _obj = _obj[key];
            // Si no se encuentra la propiedad en el objeto
            } else {
                _obj = '';
            }
        });
        return _obj;
    }

    /**
     * Accede y devuelve los datos de una función a la que hacen referencia las propiedades que se le introducen
     * @param functionData
     * @param data
     * @returns
     */
    private getDataFromFunction(functionData: string, data: unknown) {
        const params = functionData.split('(')[1].split(')')[0].replace(' ', '').split(',');
        const props = functionData.split('(')[0].split('.');

        if (params[0] !== '') {
            const paramData = params.map((param) => this.getDataFromMatch(param, data));
            const funct = this.getDataFromProperty(props, data);
            if (typeof funct === 'function') return funct(...paramData);
        }
        const funct = this.getDataFromProperty(props, data);
        if (typeof funct === 'function') return funct();
    }

    /**
     * Reemplaza los accesos a propiedades de un objeto a un acceso a la propiedad de un indice de un array
     * (Ej. <td>{{ item.name }}</td> => <td>{{ item[0].name }}</td> .... <td>{{ item[1].name }} ....)
     * @param item Item a procesar
     * @param textDataLength Numero de repeticiones del ng-repeat
     * @param repeatTextClean Contenido base para procesar y repetir
     * @returns
     */
    private replacePropertyAccessToArray(item: string, textDataLength: number, repeatTextClean) {
        let resultText = '';
        const itemRegex = new RegExp(`[^"]${item}`, 'g');
        for (let i = 0; i < textDataLength; i++) {
            resultText += repeatTextClean.replaceAll(itemRegex, (innerMatch: any) => {
                return `${innerMatch}s[${i}]`;
            });
        }
        return resultText;
    }

    private processNgRepeat(match: string, content: string, data: unknown) {
        // Obtiene la propiedad que contiene los valores de la lista en el ng-repeat (Ej. shopCart.items)
        const value: string = match.split('ng-repeat="')[1].split('"')[0];
        const inValues: string[] = value.split(' ');
        // Obtiene el contenido que se debe repetir (Ej. filas de una tabla <tr>)
        const elemType: string = match.split(' ')[0].replace('<', '');
        const repeatInitialPosition: number = content.indexOf(match);
        const repeatFinalPosition: number = content.indexOf(`</${elemType}>`, repeatInitialPosition);
        const repeatTextOriginal = content.substring(
            repeatInitialPosition,
            repeatFinalPosition + elemType.length + 3
        );
        const repeatTextClean = repeatTextOriginal.replace(/ng-repeat=".+"/, '');
        // Obtiene el numero de items de la lista para determinar cuantas veces debe repetirse
        const textDataLength: number = this.getDataFromProperty(inValues[2].split('.'), data).length;

        const resultText = this.replacePropertyAccessToArray(inValues[0], textDataLength, repeatTextClean);
        return { resultContent: resultText, originalContent: repeatTextOriginal };
    }

    private processStringConcat(_match) {
        let elem = '';
        _match.match(this.regexInterpolationContent)?.forEach((innerMatch) => {
            elem += `{{${innerMatch}}} `;
        });
        return elem.trim();
    }

    private processHtmlBinding(match: string) {
        const value = match.split('ng-bind-html="')[1].split('"')[0];
        let elem = match.replace(/ng-bind-html=".+"/, '');
        return elem.replace('><', `>{{ ${value} }}<`);
    }

    private getSplitParams(props: string[]) {
        const splitPart = props.pop();
        return {
            divider: splitPart.split('(')[1].split(')')[0],
            position: parseInt(splitPart.split('[')[1].split(']')[0]),
        };
    }
}
